import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CircleAlertIcon, CreditCardIcon, LockKeyholeIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { createCheckoutSession, redirectToCheckout } from '../../../utils/stripe';
import { getCheckoutUrl, getStudyUrl, getStudyProgram } from '../../../utils/studyAccess';
import { useStudyMembership } from '../../../utils/useStudyMembership';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
export function Checkout() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const program = getStudyProgram(searchParams.get('product'));
  const checkoutState = searchParams.get('state');
  const membership = useStudyMembership(program?.id);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUrl = `${location.pathname}${location.search}`;
  if (!program) {
    return <InvalidCheckout data-id="element-1998" data-mp="qmk06nk00ivu" />;
  }
  if (authLoading) {
    return <LoadingState data-id="element-1999" data-mp="qma4qkg6pjir" />;
  }
  if (!user) {
    navigate('/login', {
      state: {
        from: currentUrl
      },
      replace: true
    });
    return <LoadingState data-id="element-2000" data-mp="3v0qkv4q6zk0a" />;
  }
  const startCheckout = async () => {
    setStartingCheckout(true);
    setError(null);
    try {
      const lineItem = program.stripePriceId ? {
        priceId: program.stripePriceId,
        quantity: 1
      } : {
        lookupKey: program.lookupKey,
        quantity: 1
      };
      const baseUrl = window.location.origin;
      const session = await createCheckoutSession({
        lineItems: [lineItem],
        successUrl: `${baseUrl}${getCheckoutUrl(program.id, 'success')}`,
        cancelUrl: `${baseUrl}${getCheckoutUrl(program.id, 'cancelled')}`
      });
      redirectToCheckout(session.url);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : 'We could not start checkout. Please try again.');
      setStartingCheckout(false);
    }
  };
  const paid = membership.hasAccess;
  const waitingForOrder = checkoutState === 'success' && !paid;
  return <div className="min-h-screen w-full bg-[#F7F9FC] px-5 py-12 text-[#0F172A] sm:px-8 md:py-20" style={{
    fontFamily: FONT
  }} data-id="element-2001" data-mp="3penuxzpdk7oc">
      <main className="mx-auto max-w-3xl" data-id="element-2002" data-mp="3td3m8fsho27k">
        <Link to={paid ? getStudyUrl(program.id) : `/${program.id === 'rd-ready-rd-exam' ? 'rd-exam' : 'cnsc-exam'}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#52627E] transition-colors hover:text-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" data-id="element-2003" data-mp="283xx8ywjjjh">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-2004" data-mp="335pgw19khmu9" />
          Back to {program.shortName}
        </Link>

        <section className="mt-7 overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white shadow-[0_12px_36px_rgba(15,23,42,.08)]" data-id="element-2005" data-mp="2y632hoi060jv">
          <div className="bg-[#0F172A] px-6 py-7 text-white sm:px-9" data-id="element-2006" data-mp="2t5gfm90pxb80">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/65" data-id="element-2007" data-mp="2p4mshhv3o0g6">
              Secure checkout
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl" data-id="element-2008" data-mp="2k8o9k9sjjan5">
              Unlock {program.name}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/75" data-id="element-2009" data-mp="2agigewqjyn6d">
              One-time purchase, tied to this RD Ready account.
            </p>
          </div>

          <div className="p-6 sm:p-9" data-id="element-2010" data-mp="25fuidr3wp62n">
            {paid ? <div className="rounded-2xl border border-[#A7E3DA] bg-[#F0FBF9] p-6" data-id="element-2011" data-mp="21fkceeg46fns">
                <CheckCircle2Icon className="h-7 w-7 text-[#0F9E8E]" data-id="element-2012" data-mp="1wi248q9x84gu" />
                <h2 className="mt-4 text-xl font-bold" data-id="element-2013" data-mp="dcfxed0xt5s">
                  Your program is unlocked.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-2014" data-mp="4eegpzmlad9v">
                  This account has paid access to {program.name}.
                </p>
                <Link to={getStudyUrl(program.id)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0F9E8E] px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F9E8E] focus-visible:ring-offset-2" data-id="element-2015" data-mp="3rsjtik0dsvhk">
                  Open study <ArrowRightIcon className="h-4 w-4" data-id="element-2016" data-mp="33wnvz5j25sgb" />
                </Link>
              </div> : waitingForOrder ? <div className="rounded-2xl border border-[#BFDBFE] bg-[#F5F8FF] p-6" data-id="element-2017" data-mp="123hkt2ae32cs">
                <CheckCircle2Icon className="h-7 w-7 text-[#2563EB]" data-id="element-2018" data-mp="172axwtlrkrcf" />
                <h2 className="mt-4 text-xl font-bold" data-id="element-2019" data-mp="171jcucpsmc0e">Payment received.</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-2020" data-mp="1h7145bo7ahmg">
                  We are confirming your order and unlocking your study space.
                  This page updates automatically when the paid order is
                  available.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]" data-id="element-2021" data-mp="1l7l1af8rxl8l">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#2563EB]/30 border-t-[#2563EB]" data-id="element-2022" data-mp="1zvo8fryjz2yz" />
                  Confirming access…
                </span>
              </div> : <>
                {checkoutState === 'cancelled' && <div role="status" className="mb-6 flex gap-3 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm leading-relaxed text-[#92400E]" data-id="element-2023" data-mp="24vkd7tf2qyfl">
                    <CircleAlertIcon className="mt-0.5 h-5 w-5 shrink-0" data-id="element-2024" data-mp="aiug72tet93z" />
                    Checkout was cancelled. Your program has not been purchased.
                  </div>}

                <div className="flex items-start justify-between gap-5 border-b border-[#E2E8F0] pb-6" data-id="element-2025" data-mp="2emp0q55epdty">
                  <div data-id="element-2026" data-mp="2jjxmefprjzzy">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]" data-id="element-2027" data-mp="2jjfjj08linj2">
                      {program.name}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-2028" data-mp="2sm6smte0177j">
                      Quizzes, flashcards, and focused review access.
                    </p>
                  </div>
                  <p className="shrink-0 text-3xl font-extrabold" data-id="element-2029" data-mp="32ayjey7bplgv">$150</p>
                </div>

                <div className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[#52627E]" data-id="element-2030" data-mp="37akvh4bf0vo3">
                  <LockKeyholeIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#0F9E8E]" data-id="element-2031" data-mp="3ca7bflimoq0l" />
                  Your purchase is linked to{' '}
                  {user.email ?? 'your signed-in account'}. You can return to
                  your program from any device after signing in.
                </div>

                {error && <p role="alert" className="mt-6 rounded-xl border border-[#FBD5D0] bg-[#FEF2F2] p-4 text-sm font-medium text-[#B91C1C]" data-id="element-2032" data-mp="10krb7xoblmw9">
                    {error}
                  </p>}

                <button type="button" onClick={startCheckout} disabled={startingCheckout} className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-4 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" data-id="element-2033" data-mp="1obwrgdy6ah73">
                  {startingCheckout ? <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-2034" data-mp="1anlcy72mqom2" />
                      Opening secure checkout…
                    </> : <>
                      <CreditCardIcon className="h-4 w-4" data-id="element-2035" data-mp="11ozu0at8x7kr" />
                      Continue to secure checkout · $150
                    </>}
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-2036" data-mp="1tfmg3lmft11a">
                  Payment is handled by Stripe. Access is granted after a paid
                  order is recorded for this program.
                </p>
              </>}
          </div>
        </section>
      </main>
    </div>;
}
function LoadingState() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-white" data-id="element-2037" data-mp="8xya5welynuw">
      <span className="h-7 w-7 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" data-id="element-2038" data-mp="dxkns6ycm648" />
    </div>;
}
function InvalidCheckout() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-[#F7F9FC] px-5 text-center" data-id="element-2039" data-mp="5ez7ji1pxjlr">
      <div className="max-w-md rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-sm" data-id="element-2040" data-mp="1ef827toa3wg">
        <h1 className="text-2xl font-bold text-[#0F172A]" data-id="element-2041" data-mp="fe7x7ypjwc2b">
          Choose a study program
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[#52627E]" data-id="element-2042" data-mp="ag6e5ds4kcqu">
          Select RD Exam Prep or CNSC Exam Prep to continue to checkout.
        </p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white" data-id="element-2043" data-mp="3dwvnt8n082kq">
          Explore programs
        </Link>
      </div>
    </div>;
}